import React, { useEffect, useState } from "react";
import { Breadcrumb } from "@gull";
import { SimpleCard } from "@gull";
import {
  fetchAllAttendence,
  uploadAttendanceRecords,
} from "app/API/AttendenceAPI";
import { getHolidays } from "app/API/MonthlyHolidayAPI";
import moment from "moment";
import { Select } from "antd";
import { Modal, Button } from "react-bootstrap";
import Swal from "sweetalert2";

const AttendenceList = () => {
  const [attendanceList, setAttendanceList] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [status, setStatus] = useState([]);
  const [startDate, setStartDate] = useState(moment().format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
  const [filterType, setFilterType] = useState("today");
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedDate, setSelectedDate] = useState(moment().format("YYYY-MM-DD"));
  const [holidays, setHolidays] = useState([]);
  const [disabledDates, setDisabledDates] = useState(new Set());
  const toggleFilterDropdown = () => setFilterDropdownOpen(!filterDropdownOpen);

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
  };

  // Function to fetch holidays for a given month/year
  const fetchHolidays = async (year, month) => {
    try {
      const response = await getHolidays(year, month, 1, 100); // Fetch more holidays per page
      if (response && !response.error) {
        setHolidays(response.monthly_holidays || []);
        calculateDisabledDates(response.monthly_holidays || [], year, month);
      }
    } catch (error) {
      console.error("Error fetching holidays:", error);
      setHolidays([]);
      calculateDisabledDates([], year, month);
    }
  };

  // Function to calculate disabled dates (holidays + Sundays)
  const calculateDisabledDates = (holidayList, year, month) => {
    const disabled = new Set();
    
    // Add holidays to disabled dates
    holidayList.forEach(holiday => {
      if (holiday.type === 'holiday') {
        disabled.add(moment(holiday.holiday_date).format("YYYY-MM-DD"));
      }
    });
    
    // Add all Sundays of the selected month to disabled dates
    const startOfMonth = moment([year, month - 1]).startOf('month');
    const endOfMonth = moment([year, month - 1]).endOf('month');
    
    let current = startOfMonth.clone();
    while (current.isSameOrBefore(endOfMonth)) {
      if (current.day() === 0) { // Sunday is 0 in moment.js
        disabled.add(current.format("YYYY-MM-DD"));
      }
      current.add(1, 'day');
    }
    
    setDisabledDates(disabled);
  };

  // Function to check if a date should be disabled
  const isDateDisabled = (date) => {
    const selectedMoment = moment(date);
    const today = moment();
    
    // Disable future dates
    if (selectedMoment.isAfter(today, 'day')) {
      return true;
    }
    
    // Disable holidays and Sundays (existing logic)
    return disabledDates.has(date);
  };

  // Handle date change and fetch holidays if month changes
  const handleDateChange = (newDate) => {
    const oldMoment = moment(selectedDate);
    const newMoment = moment(newDate);
    
    setSelectedDate(newDate);
    
    // If month or year changed, fetch new holidays
    if (!oldMoment.isSame(newMoment, 'month')) {
      fetchHolidays(newMoment.year(), newMoment.month() + 1);
    }
  };

  // Handle modal close
  const handleModalClose = () => {
    setModalVisible(false);
    setSelectedFile(null);
    setSelectedDate(moment().format("YYYY-MM-DD"));
    setHolidays([]);
    setDisabledDates(new Set());
  };

  const handleUpload = async () => {
    if (!selectedFile || !selectedDate) {
      Swal.fire({
        icon: 'error',
        title: 'Upload Failed',
        text: 'Please select a file and a date before uploading.',
        timer: 3000,
        showConfirmButton: false
      });
      return;
    }

    // Check if selected date is disabled (holiday, Sunday, or future date)
    if (isDateDisabled(selectedDate)) {
      const selectedMoment = moment(selectedDate);
      const today = moment();
      
      let errorMessage = 'Cannot upload attendance for this date.';
      if (selectedMoment.isAfter(today, 'day')) {
        errorMessage = 'Cannot upload attendance for future dates.';
      } else if (disabledDates.has(selectedDate)) {
        errorMessage = 'Cannot upload attendance for holidays or Sundays.';
      }
      
      Swal.fire({
        icon: 'error',
        title: 'Upload Failed',
        text: errorMessage,
        timer: 3000,
        showConfirmButton: false
      });
      return;
    }
  
    try {
      const result = await uploadAttendanceRecords(selectedFile, selectedDate);
      
      if (result.err) {
        if (result.msg.includes("Duplicate entry")) {
          Swal.fire({
            icon: 'error',
            title: 'Upload Failed',
            text: `Duplicate entry for ${selectedDate}`,
            timer: 3000,
            showConfirmButton: false
          });
        } else if (result.msg.includes("with employee id")) {
          Swal.fire({
            icon: 'error',
            title: 'Upload Failed',
            text: result?.msg,
            timer: 3000,
            showConfirmButton: false
          });
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Upload Failed',
            text: result.msg,
            timer: 3000,
            showConfirmButton: false
          });
        }
      } else {
        Swal.fire({
          icon: 'success',
          title: 'Upload Successful',
          text: result.msg,
          timer: 2000,
          showConfirmButton: false
        });
        handleModalClose();
        fetchData(currentPage, itemsPerPage, startDate, endDate, status.join(","));
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Upload Failed',
        text: 'Failed to upload report. Try again or contact support.',
        timer: 3000,
        showConfirmButton: false
      });
    }
  };


  const handleFilterTypeChange = (type) => {
    setFilterType(type);
    const today = moment().format("YYYY-MM-DD");
    if (type === "month") {
      const currentMonth = moment().month();
      handleMonthChange(currentMonth + 1);
    } else if (type === "today" || type === "custom") {
      setStartDate(today);
      setEndDate(today);
    }
  };

  const handleMonthChange = (month) => {
    const year = moment().year();
    setStartDate(
      moment([year, month - 1])
        .startOf("month")
        .format("YYYY-MM-DD")
    );
    setEndDate(
      moment([year, month - 1])
        .endOf("month")
        .format("YYYY-MM-DD")
    );
  };

  const handleCustomDateChange = (type, value) => {
    if (type === "startDate") setStartDate(value);
    if (type === "endDate") setEndDate(value);
  };

  const fetchData = async (page, limit, startDate, endDate, status) => {
    try {
      const response = await fetchAllAttendence(
        page,
        limit,
        startDate,
        endDate,
        status
      );
      const attendanceData = response.data.map((record, index) => ({
        ...record,
        index: (page - 1) * limit + index + 1,
      }));
      setAttendanceList(attendanceData);
      setTotalItems(response.total);
    } catch (error) {
      console.error("Error fetching attendance data:", error);
    }
  };

  useEffect(() => {
    fetchData(currentPage, itemsPerPage, startDate, endDate, status.join(","));
  }, [currentPage, itemsPerPage, startDate, endDate, status]);

  // Fetch holidays when modal opens or selected date changes month
  useEffect(() => {
    if (modalVisible) {
      const selectedMoment = moment(selectedDate);
      fetchHolidays(selectedMoment.year(), selectedMoment.month() + 1);
    }
  }, [modalVisible]);

  // Fetch holidays when date changes to a different month
  useEffect(() => {
    if (modalVisible && selectedDate) {
      const selectedMoment = moment(selectedDate);
      const currentMonth = selectedMoment.month() + 1;
      const currentYear = selectedMoment.year();
      
      // Only fetch if we don't have holidays for this month yet
      const hasHolidaysForMonth = holidays.some(holiday => {
        const holidayMoment = moment(holiday.holiday_date);
        return holidayMoment.month() + 1 === currentMonth && holidayMoment.year() === currentYear;
      });
      
      if (!hasHolidaysForMonth) {
        fetchHolidays(currentYear, currentMonth);
      }
    }
  }, [selectedDate, modalVisible]);

  const handlePageChange = (page) => setCurrentPage(page);

  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  const handleStatusCheckboxChange = (event, statusValue) => {
    if (event.target.checked) {
      setStatus((prev) => [...prev, statusValue]);
    } else {
      setStatus((prev) => prev.filter((s) => s !== statusValue));
    }
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const getVisiblePages = () => {
    const pageRange = 5;
    const start = Math.max(1, currentPage - Math.floor(pageRange / 2));
    const end = Math.min(totalPages, start + pageRange - 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };


  // console.log("show attendence list:",attendanceList);


  return (
    <div>
      <Breadcrumb routeSegments={[{ name: "Attendence List" }]} />

      <SimpleCard>
        <div className="d-flex justify-content-end align-items-center mb-3 gap-3">
          <button
            className="btn btn-primary ml-2"
            onClick={() => setModalVisible(true)}
            style={{
              backgroundColor: "#6a0dad",
              borderColor: "#BBC5EA",
              color: "#fff",
              height: "39px",
            }}
          >
            Upload Attendence
          </button>

          <div className="dropdown">
            <button
              type="button"
              className="btn btn-primary dropdown-toggle"
              onClick={toggleFilterDropdown}
              style={{
                backgroundColor: "#BBC5EA",
                borderColor: "#BBC5EA",
                color: "#000000",
                height: "39px",
                width: "82px",
              }}
            >
              Filter&nbsp;
              <i className="fa fa-filter" aria-hidden="true"></i>
            </button>

            {filterDropdownOpen && (
              <div
                className="dropdown-menu"
                style={{
                  display: "block",
                  position: "absolute",
                  right: 0,
                  left: "auto",
                  width: "320px",
                  padding: "20px",
                  borderRadius: "10px",
                  border: "2px solid #DEE2E6",
                }}
              >
                <div style={{ marginBottom: "15px" }}>
                  <label>
                    <input
                      type="radio"
                      name="filterType"
                      value="today"
                      checked={filterType === "today"}
                      onChange={() => handleFilterTypeChange("today")}
                      style={{ marginRight: "8px" }}
                    />
                    Today
                  </label>
                  <br />
                  <label>
                    <input
                      type="radio"
                      name="filterType"
                      value="month"
                      checked={filterType === "month"}
                      onChange={() => handleFilterTypeChange("month")}
                      style={{ marginRight: "8px" }}
                    />
                    Filter by Month
                  </label>
                  <br />
                  <label>
                    <input
                      type="radio"
                      name="filterType"
                      value="custom"
                      checked={filterType === "custom"}
                      onChange={() => handleFilterTypeChange("custom")}
                      style={{ marginRight: "8px" }}
                    />
                    Filter by Custom Date
                  </label>
                </div>

                {filterType === "month" && (
                  <div>
                    <Select
                      style={{ width: "100%" }}
                      placeholder="Select Month"
                      onChange={handleMonthChange}
                      defaultValue={moment().month() + 1}
                    >
                      {moment.months().map((month, index) => (
                        <option key={index} value={index + 1}>
                          {month}
                        </option>
                      ))}
                    </Select>
                  </div>
                )}

                {filterType === "custom" && (
                  <div>
                    <label>Start Date:</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) =>
                        handleCustomDateChange("startDate", e.target.value)
                      }
                      className="form-control"
                    />
                    <label>End Date:</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) =>
                        handleCustomDateChange("endDate", e.target.value)
                      }
                      className="form-control"
                    />
                  </div>
                )}

                <div className="status-checkboxes mt-3">
                  <label className="d-flex align-items-center">
                    <input
                      type="checkbox"
                      value="present"
                      checked={status.includes("present")}
                      onChange={(e) => handleStatusCheckboxChange(e, "present")}
                      style={{ marginRight: "8px" }}
                    />
                    Present
                  </label>
                  <label className="d-flex align-items-center">
                    <input
                      type="checkbox"
                      value="absent"
                      checked={status.includes("absent")}
                      onChange={(e) => handleStatusCheckboxChange(e, "absent")}
                      style={{ marginRight: "8px" }}
                    />
                    Absent
                  </label>
                  {/* <label className="d-flex align-items-center">
                    <input
                      type="checkbox"
                      value="on leave"
                      checked={status.includes("on leave")}
                      onChange={(e) => handleStatusCheckboxChange(e, "on leave")}
                      style={{ marginRight: "8px" }}
                    />
                    On Leave
                  </label>
                  <label className="d-flex align-items-center">
                    <input
                      type="checkbox"
                      value="holiday"
                      checked={status.includes("holiday")}
                      onChange={(e) => handleStatusCheckboxChange(e, "holiday")}
                      style={{ marginRight: "8px" }}
                    />
                    Holiday
                  </label> */}
                </div>
              </div>
            )}
          </div>
        </div>

        <table className="table common-table attendance-list-table">
          <thead>
            <tr>
              <th>S.No</th>
              <th>Employee Name</th>
              <th>Attendance Date</th>
              <th>Status</th>
              <th>Check-In Time</th>
              <th>Check-Out Time</th>
              <th>Work Hours</th>
              <th>Overtime Hours</th>
            </tr>
          </thead>
          <tbody>
            {attendanceList.length > 0 ? (
              attendanceList.map((item) => (
                <tr key={item.id}>
                  <td>{item.index}</td>
                  <td>{item.employee?.first_name} {item.employee?.last_name}</td>
                  <td>{moment(item?.attendance_date).format("DD-MM-YYYY")}</td>
                  <td>{item.status}</td>
                  <td>{item.check_in_time || "-"}</td>
                  <td>{item.check_out_time || "-"}</td>
                  <td>{item.work_hours || "0"}</td>
                  <td>{item.overtime_hours || "0"}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8">No attendance records available</td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="pagination-container">
          <div className="items-per-page">
            <label>Items per page: </label>
            <select value={itemsPerPage} onChange={handleItemsPerPageChange}>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <div className="pagination-controls">
            <button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
            >
              First
            </button>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Prev
            </button>

            {getVisiblePages().map((page) => (
              <button
                key={page}
                className={currentPage === page ? "active" : ""}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
            >
              Last
            </button>
          </div>
        </div>

        <Modal show={modalVisible} onHide={handleModalClose}>
          <Modal.Header closeButton>
            <Modal.Title>Upload Attendance Records</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>Select an Excel file to upload attendance records:</p>
            <input
              type="file"
              onChange={handleFileChange}
              accept=".xlsx, .xls"
            />

<p className="mt-3">Select Date:</p>
    <input
      type="date"
      value={selectedDate}
      max={moment().format('YYYY-MM-DD')} // Prevent future date selection
      onChange={(e) => handleDateChange(e.target.value)}
      className="form-control"
      style={{
        backgroundColor: isDateDisabled(selectedDate) ? '#ffebee' : 'white',
        borderColor: isDateDisabled(selectedDate) ? '#f44336' : '#ced4da'
      }}
    />
    {isDateDisabled(selectedDate) && (
      <small className="text-danger mt-1">
        {moment(selectedDate).isAfter(moment(), 'day') 
          ? 'Future dates are not allowed for attendance upload.' 
          : 'Selected date is a holiday or Sunday. Please choose a different date.'
        }
      </small>
    )}
    <small className="text-muted mt-1">
      Note: Future dates, holidays, and Sundays are automatically disabled for attendance upload.
    </small>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleModalClose}>
              Close
            </Button>
            <Button
              variant="primary"
              onClick={handleUpload}
              disabled={!selectedFile || isDateDisabled(selectedDate)}
            >
              Upload
            </Button>
          </Modal.Footer>
        </Modal>
      </SimpleCard>
    </div>
  );
};

export default AttendenceList;
